import { 
  onAuthStateChanged, 
  signOut as firebaseSignOut,
  signInWithCustomToken
} from "firebase/auth";
import { auth, db } from "../firebase/config";

// Generates a cryptographically random string for OAuth state/nonce/PKCE
function generateRandomString(length) {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

// SHA-256 hash a string and return it as base64url (for PKCE code_challenge)
async function sha256Base64Url(str) {
  const encoder = new TextEncoder();
  const data = encoder.encode(str);
  const hash = await crypto.subtle.digest("SHA-256", data);
  return btoa(String.fromCharCode(...new Uint8Array(hash)))
    .replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

const LAMBDA_URL = process.env.REACT_APP_LAMBDA_URL || "https://4prb97zuq2.execute-api.us-west-2.amazonaws.com/prod";
const AUTH_LAMBDA_URL = (process.env.REACT_APP_AUTH_LAMBDA_URL || LAMBDA_URL).replace(/\/$/, "");
const OTP_LAMBDA_URL = (
  process.env.REACT_APP_OTP_LAMBDA_URL ||
  AUTH_LAMBDA_URL
).replace(/\/$/, "");

class AuthService {
  constructor() {
    this.auth = auth;
    this._googleProvider = null;
    // True while a sign-in flow is between Firebase auth success and the
    // users/{uid} doc being readable (the auth Lambda writes it). UserContext
    // must not treat the missing doc as a deleted account during this window.
    this._registrationInFlight = false;
  }

  isRegistrationInFlight() {
    return this._registrationInFlight;
  }

  // Dynamic getters for providers to defer SDK loading
  async getGoogleProvider() {
    if (!this._googleProvider) {
      const { GoogleAuthProvider } = await import("firebase/auth");
      this._googleProvider = new GoogleAuthProvider();
      this._googleProvider.setCustomParameters({ prompt: "select_account" });
    }
    return this._googleProvider;
  }

  async initiateGoogleRedirect(requestedReturnPath) {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) throw new Error("REACT_APP_GOOGLE_CLIENT_ID not set");

    const codeVerifier = generateRandomString(64);
    const codeChallenge = await sha256Base64Url(codeVerifier);
    const returnPath = typeof requestedReturnPath === "string" && requestedReturnPath.startsWith("/") && !requestedReturnPath.startsWith("//")
      ? requestedReturnPath
      : window.location.pathname + window.location.search;
    // Google returns state verbatim. Include the local route with the CSRF
    // nonce so the callback can recover it even if browser storage is reset.
    const state = `${generateRandomString(16)}.${btoa(unescape(encodeURIComponent(returnPath)))
      .replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "")}`;

    sessionStorage.setItem("pkce_code_verifier", codeVerifier);
    sessionStorage.setItem("pkce_state", state);
    // Keep a dedicated key through the OAuth callback. The legacy key remains
    // for flows that read it directly after email/Google completion.
    sessionStorage.setItem("google_return_path", returnPath);
    sessionStorage.setItem("nw_oauth_return_path", returnPath);

    const redirectUri = window.location.origin;

    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: "code",
      scope: "openid email profile",
      code_challenge: codeChallenge,
      code_challenge_method: "S256",
      state,
      prompt: "select_account",
    });

    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  }

  async handleGoogleOAuthCallback(code, state) {
    const storedState = sessionStorage.getItem("pkce_state");
    const codeVerifier = sessionStorage.getItem("pkce_code_verifier");

    if (!storedState || state !== storedState) {
      throw new Error("OAuth state mismatch — possible CSRF attack");
    }
    if (!codeVerifier) {
      throw new Error("Missing PKCE code_verifier");
    }

    sessionStorage.removeItem("pkce_state");
    sessionStorage.removeItem("pkce_code_verifier");

    const redirectUri = window.location.origin;

    const res = await fetch(`${AUTH_LAMBDA_URL}/exchange`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        code,
        code_verifier: codeVerifier,
        redirect_uri: redirectUri,
        invite_token: sessionStorage.getItem("nw_invite") || undefined,
      }),
    });

    const data = await res.json();

    if (!res.ok || !data.id_token) {
      throw new Error(data.error || "Token exchange failed");
    }

    return await this.signInWithGISCredential(data.id_token);
  }

  async signInWithGISCredential(idToken) {
    this._registrationInFlight = true;
    try {
      const { GoogleAuthProvider, signInWithCredential, signOut } = await import("firebase/auth");
      const googleCredential = GoogleAuthProvider.credential(idToken);
      const result = await signInWithCredential(this.auth, googleCredential);
      const userData = await this.handleUserAuth(result.user, "google");
      if (userData?.isBanned) {
        await signOut(this.auth);
        return {
          success: false,
          banned: true,
          banInfo: userData.banInfo || { reason: "Your account has been suspended", timestamp: new Date() },
        };
      }
      return { success: true, user: userData };
    } catch (error) {
      console.error("Google sign-in error:", error);
      return { success: false, error: error.message };
    } finally {
      this._registrationInFlight = false;
    }
  }

  async requestEmailOtp(email) {
    const res = await fetch(`${OTP_LAMBDA_URL}/auth/email-otp/request`, {
      method: "POST",
      body: JSON.stringify({ email }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.error || "Could not send the sign-in code");
    }
    return data;
  }

  async verifyEmailOtp(email, requestId, code) {
    this._registrationInFlight = true;
    try {
      const res = await fetch(`${OTP_LAMBDA_URL}/auth/email-otp/verify`, {
        method: "POST",
        body: JSON.stringify({
          email,
          request_id: requestId,
          code,
          invite_token: sessionStorage.getItem("nw_invite") || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.custom_token) {
        throw new Error(data.error || "Could not verify the sign-in code");
      }

      const { signOut } = await import("firebase/auth");
      const result = await signInWithCustomToken(this.auth, data.custom_token);
      const userData = await this.handleUserAuth(result.user, "email_otp", {
        email: data.email || email,
      });

      if (userData.isBanned) {
        await signOut(this.auth);
        return {
          success: false,
          banned: true,
          banInfo: userData.banInfo || { reason: "Your account has been suspended", timestamp: new Date() },
        };
      }

      return { success: true, user: userData };
    } catch (error) {
      console.error("Email OTP sign in error:", error);
      return { success: false, error: error.message };
    } finally {
      this._registrationInFlight = false;
    }
  }

  async handleUserAuth(firebaseUser, provider, profile = {}) {
    const { doc, getDoc } = await import("firebase/firestore");
    const userRef = doc(db, "users", firebaseUser.uid);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      return await this.createNewUser(firebaseUser, provider, profile);
    } else {
      return await this.updateExistingUser(firebaseUser, provider, profile);
    }
  }

  async createNewUser(firebaseUser, provider, profile = {}) {
    const idToken = await firebaseUser.getIdToken();
    const response = await fetch(`${AUTH_LAMBDA_URL}/auth/registration/complete`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        idToken,
        provider,
        invite_token: sessionStorage.getItem("nw_invite") || undefined,
      }),
    });
    const completion = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(completion.error || "Could not complete registration");
    const { doc, getDoc } = await import("firebase/firestore");
    // The Lambda owns first-write status and permissions; its write may land a
    // moment after the 200. Retry briefly before treating it as a failure.
    for (let attempt = 0; attempt < 4; attempt++) {
      if (attempt > 0) await new Promise((r) => setTimeout(r, 600));
      const registered = await getDoc(doc(db, "users", firebaseUser.uid));
      if (registered.exists()) return registered.data();
    }
    throw new Error("Registration record was not created");

    /*
    const securityHash = generateSecureHash(firebaseUser.uid);
    const userToken = generateUserToken();
    const email = firebaseUser.email || profile.email || "";
    const displayName = provider === "email_otp"
      ? ""
      : (firebaseUser.displayName || profile.displayName || email?.split("@")[0] || "Neuralward user");

    const userData = {
      uid: firebaseUser.uid,
      email,
      displayName,
      photoURL: firebaseUser.photoURL || profile.photoURL || null,
      provider: provider,
      securityHash: securityHash,
      userToken: userToken,
      groups: ["users"],
      permissions: {
        canPurchase: true,
        canViewPrices: true,
        isAdmin: false,
        isCorporateOwner: false,
      },
      accountType: "individual",
      createdAt: serverTimestamp(),
      lastLoginAt: serverTimestamp(),
      isActive: true,
      isBanned: false,
      profileComplete: provider !== "email_otp",
      requiresOnboarding: provider === "email_otp",
      authProviders: [provider],
      loginHistory: [],
    };

    await setDoc(doc(db, "users", firebaseUser.uid), userData);
    await this.addUserToDefaultGroup(firebaseUser.uid);

    return userData;
    */
  }

  // Login history is recorded SERVER-SIDE by the auth lambda (trusted request
  // IP) — the old client-side writers (ipify + arrayUnion) were removed.

  async updateExistingUser(firebaseUser, provider, profile = {}) {
    const { doc, getDoc, updateDoc, serverTimestamp } = await import("firebase/firestore");
    const userRef = doc(db, "users", firebaseUser.uid);

    const userSnap = await getDoc(userRef);
    const userData = userSnap.data();

    if (userData.isBanned) {
      return userData;
    }

    const updateData = {
      lastLoginAt: serverTimestamp(),
    };

    const email = firebaseUser.email || profile.email || "";
    const displayName = firebaseUser.displayName || profile.displayName || "";
    const photoURL = firebaseUser.photoURL || profile.photoURL || "";

    const incomingIsEmailOnlyName =
      provider === "email_otp" && displayName && email && displayName.toLowerCase() === email.toLowerCase();
    const hasGoogleProfile =
      userData.provider === "google" ||
      userData.authProviders?.includes?.("google") ||
      !!userData.photoURL;

    if (displayName && !incomingIsEmailOnlyName && (!userData.displayName || userData.displayName !== displayName)) {
      updateData.displayName = displayName;
    }

    if (photoURL && (!userData.photoURL || userData.photoURL !== photoURL)) {
      updateData.photoURL = photoURL;
    }

    if (email && (!userData.email || userData.email !== email)) {
      updateData.email = email;
    }

    if (!userData.authProviders?.includes?.(provider)) {
      const { arrayUnion } = await import("firebase/firestore");
      updateData.authProviders = arrayUnion(provider);
    }

    if (provider === "google") {
      updateData.provider = "google";
      const effectiveDisplayName = (updateData.displayName || userData.displayName || "").trim();
      const effectiveEmail = (email || userData.email || "").trim().toLowerCase();
      const hasRequiredProfileFields = !!effectiveDisplayName && effectiveDisplayName.toLowerCase() !== effectiveEmail;
      updateData.profileComplete = hasRequiredProfileFields;
      updateData.requiresOnboarding = !hasRequiredProfileFields;
    } else if (provider === "email_otp" && !hasGoogleProfile) {
      updateData.profileComplete = userData.profileComplete ?? false;
      updateData.requiresOnboarding = userData.requiresOnboarding ?? true;
    }

    await updateDoc(userRef, updateData);

    const updatedSnap = await getDoc(userRef);
    return updatedSnap.data();
  }

  async addUserToDefaultGroup(userId) {
    const { doc, getDoc, setDoc, updateDoc, arrayUnion, serverTimestamp } = await import("firebase/firestore");
    const groupRef = doc(db, "groups", "users");

    try {
      const groupSnap = await getDoc(groupRef);

      if (!groupSnap.exists()) {
        await setDoc(groupRef, {
          name: "users",
          displayName: "Users",
          description: "Default user group",
          permissions: {
            canPurchase: true,
            canViewPrices: true,
          },
          members: [userId],
          createdAt: serverTimestamp(),
        });
      } else {
        const groupData = groupSnap.data();
        if (!groupData.members || !groupData.members.includes(userId)) {
          await updateDoc(groupRef, {
            members: arrayUnion(userId),
          });
        }
      }
    } catch (error) {
      console.error("Error adding user to default group:", error);
    }
  }

  async signOut() {
    try {
      await firebaseSignOut(this.auth);
      return { success: true };
    } catch (error) {
      console.error("Sign out error:", error);
      return { success: false, error: error.message };
    }
  }

  getCurrentUser() {
    return this.auth.currentUser;
  }

  onAuthStateChange(callback) {
    return onAuthStateChanged(this.auth, callback);
  }

  async checkUserPermission(userId, permission) {
    const { doc, getDoc } = await import("firebase/firestore");
    const userRef = doc(db, "users", userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) return false;

    const userData = userSnap.data();
    if (userData.isBanned) return false;

    return userData.permissions[permission] || false;
  }

  async checkUserBanStatus(userId) {
    try {
      const { doc, getDoc } = await import("firebase/firestore");
      const userRef = doc(db, "users", userId);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) return { isBanned: false };

      const userData = userSnap.data();

      if (userData.isBanned) {
        return {
          isBanned: true,
          banInfo: userData.banInfo || {
            reason: "Your account has been suspended",
            timestamp: new Date(),
          },
        };
      }

      return { isBanned: false };
    } catch (error) {
      console.error("Error checking ban status:", error);
      return { isBanned: false };
    }
  }

  async getUserGroups(userId) {
    const { doc, getDoc } = await import("firebase/firestore");
    const userRef = doc(db, "users", userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) return [];

    const userData = userSnap.data();
    return userData.groups || [];
  }

  async validateUserToken(userId, token) {
    const { doc, getDoc } = await import("firebase/firestore");
    const userRef = doc(db, "users", userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) return false;

    const userData = userSnap.data();
    if (userData.isBanned) return false;

    return userData.userToken === token;
  }

  async refreshUserProfile(userId) {
    try {
      const { doc, getDoc, updateDoc, serverTimestamp } = await import("firebase/firestore");
      const currentUser = this.auth.currentUser;
      if (!currentUser || currentUser.uid !== userId) {
        throw new Error("User must be authenticated to refresh their profile");
      }

      await currentUser.reload();

      const userRef = doc(db, "users", userId);
      const existingSnap = await getDoc(userRef);
      const existingData = existingSnap.exists() ? existingSnap.data() : {};
      const updateData = {
        displayName: currentUser.displayName || existingData.displayName || "",
        photoURL: currentUser.photoURL || existingData.photoURL || null,
        email: currentUser.email || existingData.email || "",
        profileLastUpdated: serverTimestamp(),
      };

      await updateDoc(userRef, updateData);

      const updatedSnap = await getDoc(userRef);
      return updatedSnap.data();
    } catch (error) {
      console.error("Error refreshing user profile:", error);
      throw error;
    }
  }
}

const authService = new AuthService();
export default authService;
